import PropTypes from 'prop-types';
import { StatisticsLayout } from 'views/components';
import isEmpty from 'lodash/isEmpty';
import { statisticsActions } from 'core/Statistics';
import { useTranslation } from 'react-i18next';
import {
    BarChart,
    Card,
    Container,
    InfoCard,
    PieChart
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import React, { useMemo, useState } from 'react';
import {
    clearObject,
    isLocked,
    objectToChart
} from 'shared/utils';
import {
    useDispatch,
    useSelector
} from 'hooks';

const Quizzes  = ({
    tabController
}) => {

    const { t } = useTranslation();
    const {
        loading,
        quizzes,
        quizzesDownload,
        quizzesDownloading
    } = useSelector('statistics');
    const { user } = useSelector('account');
    const dispatch = useDispatch();
    const [, setCurrentFilters] = useState({});
    
    const extractDownloadHeaders = structure => {
        if (isEmpty(structure)) return [];
        return structure.map(item => ({
            key: item.question_uuid,
            label: item.content
        }));
    };

    const downloadHeaders = useMemo(() => [{
        key: 'created_at',
        label: t('Fecha')
    }, {
        key: 'patient_identification',
        label: t('DNIPaciente')
    }, {
        key: 'patient_last_name',
        label: t('Apellido')
    }, {
        key: 'patient_first_name',
        label: t('Nombre')
    }, ...(extractDownloadHeaders(quizzesDownload?.structure))
    ], [quizzesDownload]);

    const downloadController = {
        data: quizzesDownload?.data,
        downloading: quizzesDownloading,
        fileName: t('Encuestas'),
        headers: downloadHeaders,
        onDownload: filters => dispatch(statisticsActions.getStatisticsQuizzesDownload(filters))
    };

    const onFiltersChange = filters => {
        setCurrentFilters(filters);
        dispatch(statisticsActions.getStatisticsQuizzes(clearObject({
            ...filters
        })));
    };

    const {
        correct_answers_score,
        question_answers,
        total_answers,
        total_correct_answers,
        total_quizzes
    } = quizzes;

    return (
        <StatisticsLayout
            downloadController={ !isLocked(user, 'download_reports') ? downloadController : undefined }
            filterPatients={ ['quiz', 'workplace'] }
            filterProfessionals={ false }
            filterSpecialties={ false }
            loading={ loading }
            tabController={ tabController }
            onFiltersChange={ onFiltersChange }
        >
            <Row>
                <Col md="6" xs="12">
                    <InfoCard
                        caption={ t('Cantidad de encuestas respondidas') }
                        icon="summarize"
                        title={ (total_quizzes ?? 0).toString() }
                    />
                </Col>
                {
                    !!total_quizzes &&
                    <>
                        <Col md="6" xs="12">
                            <InfoCard
                                caption={ t('Porcentaje de respuestas correctas') }
                                icon="percent"
                                title={ (correct_answers_score ?? 0).toString() }
                            />
                        </Col>
                        <Col md="6" xs="12">
                            <InfoCard
                                caption={ t('Respuestas correctas') }
                                icon="done"
                                title={ `${ total_correct_answers } / ${ total_answers }` }
                            />
                        </Col>
                        <Col md="6" xs="12" />
                        {
                            question_answers.map(({
                                answers,
                                correct_answer,
                                question,
                                total_answers,
                                total_correct_answers
                            }, index) => {
                                const chartProps = {
                                    config: [{
                                        dataKey: 'value',
                                        fontSize: '11px',
                                        label: question
                                    }],
                                    data: objectToChart(answers),
                                    height: 300,
                                    labelMaxLength: 12,
                                    selected: correct_answer
                                };
                                return (
                                    <Col key={ `${ question }-${ index }` } sm="6" xs="12">
                                        <Card
                                            overflowX="auto"
                                            title={ `${ question }${ !!correct_answer ? ` (${ total_correct_answers }/${ total_answers })` : '' }` }
                                        >
                                            <Container minWidth="500px">
                                                {
                                                    Object.keys(answers).length >= 5
                                                        ? <PieChart { ...chartProps } />
                                                        : <BarChart { ...chartProps } />
                                                }
                                            </Container>
                                        </Card>
                                    </Col>
                                );
                            })
                        }
                    </>
                }
            </Row>
        </StatisticsLayout>
    );
};

Quizzes.propTypes = {
    tabController: PropTypes.object
};

export default Quizzes;
